import { isEmpty, isEqual } from "lodash";
import { ReactElement, useEffect, useState } from "react";
import { Button, OverlayTrigger, Popover } from "react-bootstrap";
import { isMobile } from "react-device-detect";
import { AiFillHeart, AiOutlineHeart } from "react-icons/ai";
import {
  eInteraction,
  Interaction,
  StoreInteractionRequest,
} from "../../../@types/Interaction";
import { Post, Reaction, ReactionsIconsByContent } from "../../../@types/Posts";
import { User } from "../../../@types/Users";
import usePostContext from "../../../context/PostContext";
import OverlayDescription from "./OverlayDescription";
import useInteraction from "../../../hooks/useInteraction";

interface Props {
  post: Post;
  user: User;
}

const iconSizeRI = 20; // RI = React Icons.
const iconSizeEmoji = 18;

const smallIconSizeRI = 17;
const smallIconSizeEmoji = 14;

function getEmojiAsIcon(emoji: string, size: number): ReactElement {
  return <span style={{ fontSize: size }}>{emoji}</span>;
}

export const reactionsIconsByContent: ReactionsIconsByContent = {
  love: {
    checked: <AiFillHeart size={iconSizeRI} color="#DF704D" />,
    unchecked: <AiOutlineHeart size={iconSizeRI} />,
    smallChecked: <AiFillHeart size={smallIconSizeRI} color="#DF704D" />,
  },
  funny: {
    checked: getEmojiAsIcon("😆", iconSizeEmoji),
    unchecked: getEmojiAsIcon("😆", iconSizeEmoji),
    smallChecked: getEmojiAsIcon("😆", smallIconSizeEmoji),
  },
  cry: {
    checked: getEmojiAsIcon("😢", iconSizeEmoji),
    unchecked: getEmojiAsIcon("😢", iconSizeEmoji),
    smallChecked: getEmojiAsIcon("😢", smallIconSizeEmoji),
  },
  up: {
    checked: getEmojiAsIcon("🚀", iconSizeEmoji),
    unchecked: getEmojiAsIcon("🚀", iconSizeEmoji),
    smallChecked: getEmojiAsIcon("🚀", smallIconSizeEmoji),
  },
  down: {
    checked: getEmojiAsIcon("💣", iconSizeEmoji),
    unchecked: getEmojiAsIcon("💣", iconSizeEmoji),
    smallChecked: getEmojiAsIcon("💣", smallIconSizeEmoji),
  },
};

const reactions: Reaction[] = [
  {
    content: "love",
    description: "Amei",
    icons: {
      checked: reactionsIconsByContent["love"].checked,
      unchecked: reactionsIconsByContent["love"].unchecked,
    },
    status: false,
  },
  {
    content: "funny",
    description: "Engraçado",
    icons: {
      checked: reactionsIconsByContent["funny"].checked,
      unchecked: reactionsIconsByContent["funny"].unchecked,
    },
    status: false,
  },
  {
    content: "cry",
    description: "Triste",
    icons: {
      checked: reactionsIconsByContent["cry"].checked,
      unchecked: reactionsIconsByContent["cry"].unchecked,
    },
    status: false,
  },
  {
    content: "up",
    description: "Alta",
    icons: {
      checked: reactionsIconsByContent["up"].checked,
      unchecked: reactionsIconsByContent["up"].unchecked,
    },
    status: false,
  },
  {
    content: "down",
    description: "Baixa",
    icons: {
      checked: reactionsIconsByContent["down"].checked,
      unchecked: reactionsIconsByContent["down"].unchecked,
    },
    status: false,
  },
];

export default function ReactionButton({ post, user }: Props) {
  const [isTrigged, setIsTrigged] = useState<boolean>(false);
  const [showTrigger, setShowTrigger] = useState<boolean>(false);
  const [actualReaction, setActualReaction] = useState<Reaction>(reactions[0]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { interactions, setInteractions } = usePostContext();
  const { create, doDestroy, doUpdate } = useInteraction();

  const handleShowTrigger = () => setShowTrigger(true);
  const handleHideTrigger = () => setShowTrigger(false);

  useEffect(() => {
    async function determineActualReaction() {
      if (!isEmpty(interactions)) {
        const interaction = interactions.find(
          (interaction) =>
            interaction.user_id === user.id &&
            interaction.type === eInteraction.REACTION
        );

        if (user && interaction) {
          const reaction = reactions.find(
            (reaction) => reaction.content === interaction.content
          );

          if (!isEmpty(reaction)) {
            const newReaction = {
              ...reaction,
              status: true,
              interactionId: interaction.id,
            };
            setActualReaction(newReaction);
          }
        }
      }
    }

    determineActualReaction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactions]);

  useEffect(() => {
    if (isMobile && showTrigger && !isTrigged) {
      setShowTrigger(false);
    }
  }, [showTrigger, isTrigged]);

  async function destroyActualInteraction() {
    setIsLoading(true);
    const actualInteractionId = actualReaction.interactionId;
    const defaultReaction = reactions[0];
    const newInteractionsContext: Interaction[] = [];
    setActualReaction(defaultReaction);

    await doDestroy(actualInteractionId);

    interactions.forEach((interaction) => {
      if (interaction.id !== actualInteractionId) {
        newInteractionsContext.push(interaction);
      }
    });

    setInteractions(newInteractionsContext);
    setIsLoading(false);
  }

  async function handleInteract(reaction: Reaction) {
    setIsLoading(true);

    setActualReaction({
      ...reaction,
      status: true,
    });

    const requestData: StoreInteractionRequest = {
      content: reaction.content,
      interactable_id: post.id,
      interactable_type: "post",
      type: eInteraction.REACTION,
    };
    const createdInteraction = await create(requestData);

    if (createdInteraction) {
      const newReaction = {
        ...reaction,
        status: true,
        interactionId: createdInteraction.id,
      };
      setActualReaction(newReaction);
      setInteractions([...interactions, { ...createdInteraction, user }]);
    }

    setIsLoading(false);
  }

  async function updateActualReaction(reaction: Reaction) {
    if (reaction.content === actualReaction.content) {
      return;
    }
    setIsLoading(true);
    const actualInteractionId = actualReaction.interactionId;
    const newInteractionsState: Interaction[] = [];

    const tempReaction = {
      ...reaction,
      status: true,
    };
    setActualReaction(tempReaction);

    const updatedInteraction = await doUpdate(actualInteractionId, {
      content: reaction.content,
    });

    if (updatedInteraction) {
      const newReaction = {
        ...reaction,
        status: true,
        interactionId: actualInteractionId,
      };

      setActualReaction(newReaction);

      interactions.forEach((interaction) => {
        if (isEqual(interaction.id, actualInteractionId)) {
          interaction.content = reaction.content;
          newInteractionsState.push(interaction);
        } else {
          newInteractionsState.push(interaction);
        }
      });
      setInteractions(newInteractionsState);
    }

    setIsLoading(false);
  }

  async function handleOverlayReaction(reaction: Reaction) {
    if (isLoading) {
      return;
    }

    const isChecked = actualReaction.status;

    if (isChecked) {
      await updateActualReaction(reaction);
    } else {
      await handleInteract(reaction);
    }
  }

  async function handlePrincipalInteraction() {
    if (isLoading) {
      return;
    }

    const isChecked = actualReaction.status;
    const interactionId = actualReaction.interactionId;

    if (isChecked && interactionId) {
      await destroyActualInteraction();
    } else {
      if (!isMobile) {
        await handleInteract(actualReaction);
      }
    }
  }

  function overlay() {
    if (isMobile && actualReaction.status) {
      return <></>;
    }

    return (
      <Popover
        onMouseEnter={handleShowTrigger}
        onMouseLeave={handleHideTrigger}
        style={{
          borderRadius: 15,
          margin: 0,
          zIndex: 999,
          width: "100%",
          maxWidth: isMobile ? 250 : 360,
          backgroundColor: "var(--bg-3)",
          border: "1px solid var(--border)",
        }}
      >
        <div className="animated-fast fadeInDown">
          <Popover.Body>
            {reactions.map((reaction, i) => {
              return (
                <OverlayTrigger
                  key={i}
                  delay={{ show: 500, hide: 200 }}
                  overlay={OverlayDescription({
                    description: reaction.description,
                  })}
                >
                  <Button
                    className={isMobile ? "mg-r-5 mg-t-10" : "mg-r-5"}
                    style={{
                      borderRadius: 15,
                      maxHeight: 35,
                      maxWidth: 60,
                      justifyContent: "center",
                      display: "inline-flex",
                      alignItems: "center",
                    }}
                    variant="light"
                    onClick={() => handleOverlayReaction(reaction)}
                  >
                    {reaction.icons.checked}
                  </Button>
                </OverlayTrigger>
              );
            })}
          </Popover.Body>
        </div>
      </Popover>
    );
  }

  return (
    <OverlayTrigger
      delay={{ show: 200, hide: 200 }}
      overlay={overlay()}
      show={showTrigger || isTrigged}
      onToggle={(value) => setIsTrigged(value)}
    >
      <Button
        style={{
          borderRadius: 15,
          maxHeight: 30,
          maxWidth: 60,
          display: "inline-flex",
          justifyContent: "center",
          alignItems: "center",
        }}
        variant="light"
        onClick={() => handlePrincipalInteraction()}
      >
        {actualReaction.status
          ? actualReaction.icons.checked
          : actualReaction.icons.unchecked}
      </Button>
    </OverlayTrigger>
  );
}
